import type { CasinoCategory } from '../types/game'
import { api } from './api-client'
import { fetchRecentPlayed } from './recentPlayed'

// The casino category list is shared by the casino pages and the side menu.
// Centralising the fetch keeps the on-open hook and the sidebar preloader in
// sync (same endpoint, same "favorites" augmentation for logged-in users).

const RECENT_CATEGORY_ID = 'recent_played'

// The backend only returns the "recent" category once an account has play
// history, but the tab should always be available when signed in. When the API
// omits it we inject this fallback, pointing its icons at the remotely-hosted
// category assets (theme- and state-specific, like every other category).
const RECENT_ICON_BASE =
  'https://sfgdefe0923.fun88adrods.online/mexicoimages/categories'

const recentCategory: CasinoCategory = {
  id: RECENT_CATEGORY_ID,
  category: 'RECIENTES',
  icon_dark_active: `${RECENT_ICON_BASE}/recent_dark_active.webp`,
  icon_dark_off: `${RECENT_ICON_BASE}/recent_dark_off.webp`,
  icon_light_active: `${RECENT_ICON_BASE}/recent_light_active.webp`,
  icon_light_off: `${RECENT_ICON_BASE}/recent_light_off.webp`,
  count: 0,
}

export const needsCategoryRefresh = (
  categories: CasinoCategory[],
  username: string,
) => {
  // The backend returns a `recent_played` category for logged-in accounts, so
  // its presence marks the list as fetched-while-logged-in. Refetch when the
  // list is empty or its auth state no longer matches the current user.
  const hasRecentPlayed = categories.some(c => c.id === 'recent_played')
  return (
    categories.length === 0 ||
    (hasRecentPlayed && !username) ||
    (!hasRecentPlayed && username)
  )
}

// De-dupes concurrent fetches: multiple consumers (casino page, footer links,
// sidebar preloader) can mount on the same tick with an empty store and each
// pass the guard before any setCategories lands. They share this one in-flight
// promise instead of firing duplicate requests, cleared in finally so the next
// guard miss can refetch. The promise is keyed by the account it was issued for:
// the guest list (no `favorites`) and a logged-in list (with `favorites`) differ,
// so a caller for one account must never reuse an in-flight request for another
// (e.g. a guest fetch still resolving when the user logs in).
let inFlight: { account: string; promise: Promise<CasinoCategory[] | null> } | null = null

// The dedicated recent-played endpoint returns the authoritative play count for
// the signed-in account. The category list's own `recent_played` entry carries
// a stale/zero count (or is client-injected with 0), so the tab badge is driven
// from here instead. (The same endpoint also serves the tab's game list — see
// useGames — so both go through the shared fetchRecentPlayed request, which
// collapses the two into one GET when they overlap.)
const requestRecentCount = async (username: string): Promise<number | null> => {
  try {
    const data = await fetchRecentPlayed(username)
    return data?.count ?? null
  } catch {
    // A failure here must not sink the whole category list — fall back to the
    // count already on the recent entry (backend value or the injected 0).
    return null
  }
}

const requestGameCategories = async (
  username: string,
): Promise<CasinoCategory[] | null> => {
  // Guests never get a recent tab, so only the logged-in path pays for the
  // extra recent-count request. Return the category list untouched for guests.
  if (!username) {
    const res = await api.get<CasinoCategory[]>('/games/categories.php', {
      account: username,
    })
    return res.data ?? null
  }

  // Both requests are independent, so fire them in parallel.
  const [res, recentCount] = await Promise.all([
    api.get<CasinoCategory[]>('/games/categories.php', { account: username }),
    requestRecentCount(username),
  ])

  if (!res.data) return null

  // For logged-in users, surface the recent tab next to POPULAR (position 2)
  // when the backend didn't already include it.
  const withRecent = res.data.some(c => c.id === RECENT_CATEGORY_ID)
    ? res.data
    : [...res.data.slice(0, 2), recentCategory, ...res.data.slice(2)]

  // The favorites category is now returned by the API directly, so it no longer
  // needs to be appended here on the client.

  // Populate the recent tab badge from the dedicated recent endpoint. Leave the
  // existing count in place when that request failed or returned no count.
  if (recentCount == null) return withRecent

  return withRecent.map(c =>
    c.id === RECENT_CATEGORY_ID ? { ...c, count: recentCount } : c,
  )
}

// The shared request is intentionally NOT tied to any caller's AbortSignal:
// aborting one consumer (e.g. casino page unmount on navigation) must not reject
// the others awaiting the same promise. The short-lived shared fetch relies on
// the guard, and every consumer already discards a late result (`cancelled` flag
// / store guard), so a post-unmount completion is harmless.
export const fetchGameCategoriesData = (
  username: string,
): Promise<CasinoCategory[] | null> => {
  if (inFlight && inFlight.account === username) return inFlight.promise

  const promise = requestGameCategories(username).finally(() => {
    // Only clear if this is still the current in-flight request (a newer
    // request for a different account may have replaced it).
    if (inFlight?.promise === promise) inFlight = null
  })

  inFlight = { account: username, promise }

  return promise
}
